/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable  */
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { BlockUI, AppAlert, Ago, ConfirmModal } from "@advapacs/common";
import { useTranslation } from "react-i18next";
import { supported } from "@github/webauthn-json";
import LaddaButton from "react-ladda-button";
import {
  deleteWebAuthnRegistration,
  executeRegisterRequest,
  loadWebAuthnRegistrations,
} from "./ducks/operations";
import { WebAuthnSettingActions } from "./ducks/slices";
import { webAuthnSettingSelector } from "./ducks/selectors";
import { IRootState } from "../../../config/reducers";
import { useAppDispatch } from "../../../config/store";

const WebAuthnRegisterForm: FunctionComponent = () => {
  const {
    credentialName,
    webAuthns,
    webAuthnRequesting,
    loading,
    error,
    webAuthnError,
  } = useSelector(webAuthnSettingSelector);
  const [authenticationTypeForm, setAuthenticationTypeForm] = useState("");
  const [authenticationType, setAuthenticationType] = useState("");
  const [showSetUpModal, setShowSetUpModal] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["2fa", "general"]);
  const { isMobileOnly } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  useEffect(() => {
    dispatch(WebAuthnSettingActions.clearError());
    dispatch(loadWebAuthnRegistrations());
  }, []);
  useEffect(() => {
    if (webAuthnError) {
      dispatch(WebAuthnSettingActions.updateCredentialName(""));
      setAuthenticationTypeForm("");
      setAuthenticationType("");
      const { code, message } = webAuthnError;
      switch (code) {
        case 0:
          dispatch(WebAuthnSettingActions.failure(message || t("timeout")));
          break;
        case 11:
          dispatch(WebAuthnSettingActions.failure(t("device_was_registered")));
          break;
        default:
          dispatch(WebAuthnSettingActions.failure(t("unknown_error")));
          break;
      }
    }
  }, [dispatch, t, webAuthnError]);

  const onStartSetUpDevice = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setShowSetUpModal(true);
  };

  const registerCredential = () => {
    dispatch(WebAuthnSettingActions.clearError());
    if (supported()) {
      if (credentialName && authenticationType) {
        dispatch(executeRegisterRequest(credentialName, authenticationType));
      }
    } else {
      dispatch(
        WebAuthnSettingActions.failure(t("brower_not_support_for_webauthn"))
      );
    }
  };

  const deleteDeviceClick = useCallback(
    (device) => {
      dispatch(WebAuthnSettingActions.clearError());
      dispatch(deleteWebAuthnRegistration(device));
    },
    [dispatch]
  );

  const onCloseRegisterModal = () => {
    setShowSetUpModal(false);
    setAuthenticationTypeForm("");
    setAuthenticationType("");
    dispatch(WebAuthnSettingActions.finishRegisteringCredential());
  };

  const onConfirmRegisterModal = () => {
    setShowSetUpModal(false);
    setAuthenticationType(authenticationTypeForm);
    dispatch(WebAuthnSettingActions.clearError());
    dispatch(WebAuthnSettingActions.startRegisteringCredential());
  };

  const bodyRegisterConfirmModal = () => (
    <div className="row">
      <div className="col">
        <h6>{t("please_select_authentication_type")}</h6>
        <div className="col">
          <div>
            <label className="form-check-label" htmlFor="authenticationType1">
              <input
                type="radio"
                id="authenticationType1"
                name="authenticationType"
                onChange={() => setAuthenticationTypeForm("PLATFORM")}
                checked={authenticationTypeForm === "PLATFORM"}
              />
              <span className="ml-1">{t("platform")}</span>
            </label>
          </div>
          <div className="ml-3">{t("platform_authenticator_info")}</div>
          <AppAlert.SimpleAlert
            message={t("platform_authenticator_warn")}
            variant="warning"
          />
        </div>

        <div className="col">
          <div>
            <input
              type="radio"
              id="authenticationType2"
              name="authenticationType"
              onChange={() => setAuthenticationTypeForm("CROSS_PLATFORM")}
              checked={authenticationTypeForm === "CROSS_PLATFORM"}
            />
            <label className="form-check-label" htmlFor="authenticationType2">
              <span className="ml-1">{t("cross_platform")}</span>
            </label>
          </div>
          <div className="ml-3">{t("cross_platform_authenticator_info")}</div>
        </div>
      </div>
    </div>
  );

  return (
    <BlockUI isBlocked={loading}>
      <div className="row">
        <div className="col">
          <ConfirmModal
            size="lg"
            show={showSetUpModal}
            disableConfirmBtn={!authenticationTypeForm}
            body={bodyRegisterConfirmModal()}
            onClose={onCloseRegisterModal}
            cancel={onCloseRegisterModal}
            confirm={onConfirmRegisterModal}
          />
          <div className={`${isMobileOnly ? "" : "m-2"} 2fa-container`}>
            <div className="row">
              <div className="col-md-4">
                <h4>{t("register_web_authn_device")}</h4>
                <p>{t("register_web_authn_description")}</p>
              </div>
              <div className="col-md-6 col-lg-4 col-xl-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onStartSetUpDevice}
                >
                  {t("setup_web_authn_device")}
                </button>
                <div>
                  <div className="col registered-devices mt-2 pl-0">
                    <h5 className="border-bottom mb-1">
                      {t("web_authn_devices")} ({webAuthns.length})
                    </h5>
                    {webAuthns.map((credential) => (
                      <div className="row">
                        <div className="col-4 text-justify">
                          {credential.name}
                        </div>
                        <div className="col-7 text-justify">
                          <Ago
                            utc={new Date(credential.createdAt).toUTCString()}
                          />
                        </div>
                        <div className="col-1 actions">
                          <i
                            className="text-danger icon-trash"
                            onClick={() => deleteDeviceClick(credential)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {webAuthnRequesting && (
                    <div className="col mt-2">
                      <div className="row">
                        <label className="label-bold" htmlFor="name">
                          {t("credential_name")}
                          {authenticationType === "CROSS_PLATFORM" && (
                            <div className="text-danger">
                              {t("insert_usb_key")}
                            </div>
                          )}
                          <input
                            id="name"
                            type="text"
                            className="form-control"
                            required
                            autoComplete="off"
                            onChange={(e) =>
                              dispatch(
                                WebAuthnSettingActions.updateCredentialName(
                                  e.target.value
                                )
                              )
                            }
                          />
                        </label>
                      </div>
                      <div className="row">
                        <LaddaButton
                          type="button"
                          className="btn btn-primary"
                          onClick={registerCredential}
                          disabled={credentialName === ""}
                          loading={loading}
                        >
                          {t("register")}
                        </LaddaButton>
                        <button
                          type="button"
                          className="btn btn-light ml-2"
                          onClick={() => onCloseRegisterModal()}
                        >
                          {t("general:cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {error && <AppAlert.SimpleAlert message={error} variant="danger" />}
          </div>
        </div>
      </div>
    </BlockUI>
  );
};

export default WebAuthnRegisterForm;
